/**
 * Email validation utilities
 * AIDEV-NOTE: Consolidated email validation logic for consistent behavior across the app
 */

/**
 * Standard email validation regex pattern
 * Matches: user@domain.tld
 * - At least one character before @
 * - At least one character between @ and .
 * - At least one character after the last .
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Check if a string is a valid email address
 * @param email - The string to validate
 * @returns true if the email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const trimmed = email.trim();
  return EMAIL_REGEX.test(trimmed);
};

/**
 * RFC 5322 `atext` in the local part (dot-separated atoms), a plain LDH domain with at least one
 * dot. ASCII only, on purpose: the point is that what is printed is what is read.
 */
const PLAIN_ASCII_EMAIL_REGEX =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

/** The longest address `isSafeToDisplayEmail` accepts. A longer one is masked rather than printed. */
export const MAX_DISPLAYED_EMAIL_LENGTH = 64;

/**
 * Whether an address that arrived as URL text can be printed whole without it reading as something
 * else: ASCII atext local part, plain ASCII domain, at most 64 characters in all. `isValidEmail`
 * alone is far too loose for this: its `[^\s@]` classes admit bidi overrides (U+202E), zero-width
 * characters, fullwidth look-alikes and homoglyphs, none of which a reader can tell apart from the
 * real thing. Anything that fails is shown masked (`maskEmail`).
 */
export const isSafeToDisplayEmail = (email: string): boolean =>
  email.length <= MAX_DISPLAYED_EMAIL_LENGTH && PLAIN_ASCII_EMAIL_REGEX.test(email);

/**
 * Check if a string looks like an email (contains @)
 * Useful for detecting when user is attempting to type an email
 * @param value - The string to check
 * @returns true if the string contains @, false otherwise
 */
export const looksLikeEmail = (value: string): boolean => {
  return value.includes('@');
};

/**
 * Default error message for invalid email
 */
export const INVALID_EMAIL_MESSAGE = 'Please enter a valid email address';

/**
 * `j***@example.com`. A single-character local part collapses to `*@domain`, so
 * the mask never reveals the whole local part by accident.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the masked address is the ONLY form of the
 * email the /auth/link landing screen puts on screen. The visitor needs enough
 * to recognise their own address, and someone who shoulder-surfed the link
 * should not be handed a full address they did not already have. Anything
 * unparseable (no `@`, empty local part, empty string) masks WHOLE rather than
 * falling back to printing the raw value.
 */
export const maskEmail = (email: string): string => {
  const at = email.lastIndexOf('@');
  if (at <= 0) {
    return '***';
  }
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  return `${local.length > 1 ? `${local[0]}***` : '*'}@${domain}`;
};
